import Card from "components/card";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Pagination from "components/pagination/Pagination";
import OrganizationEventService from "services/oraganizationEvent.service";
import Loader from "../../../assets/img/loader.gif";
import Avatar from "react-avatar";
import moment from "moment";
import { FiSearch } from "react-icons/fi";
import Toast from "components/toaster";
import { FaTrash } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import tabCache from "utils/tabCache";

const columnHelper = createColumnHelper();

const ListGarages = () => {
  const [searchParams] = useSearchParams();
  const startDateParam = searchParams.get("startDate");
  const endDateParam = searchParams.get("endDate");

  const dateRangeFromParams = React.useMemo(() => {
    if (!startDateParam || !endDateParam) {
      return null;
    }

    const start = new Date(startDateParam);
    const end = new Date(endDateParam);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }

    const normalizedStart = new Date(start);
    normalizedStart.setHours(0, 0, 0, 0);

    const normalizedEnd = new Date(end);
    normalizedEnd.setHours(23, 59, 59, 999);

    return {
      startDate: normalizedStart.toISOString(),
      endDate: normalizedEnd.toISOString(),
    };
  }, [startDateParam, endDateParam]);

  const profileRef = useRef();
  const activateRef = useRef();
  const prevSearchTextRef = useRef("");
  const [sorting, setSorting] = React.useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(3);
  const [popupOpen, setPopupOpen] = useState(false);
  const [profilePopup, setProfilePopup] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  // Initialize from cache - show loader only on first load
  const [isLoading, setIsLoading] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [id, setId] = useState("");
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [popupOpenDelete, setPopupOpenDelete] = useState(false);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [isFilterChanging, setIsFilterChanging] = useState(false);

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  // Create debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchText(value);
    }, 500),
    []
  );

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  const handleOpenAvtive = async (id, active) => {
    setSelectedUser(active);
    setId(id);
    setPopupOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleOpen = async (item) => {
    setIsViewLoading(true);
    setId(item);
    try {
      const body = {
        id: item,
      };
      const response =
        await OrganizationEventService.getOrganizationEventsDetails(body);
      setProfilePopup(true);
      setProfileData(response.data.data);
      document.body.style.overflow = "hidden";
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setIsViewLoading(false);
      setId("");
    }
  };

  const handleOpenDelete = async (id, active, data) => {
    setSelectedUser(data);
    setId(id);
    setPopupOpenDelete(true);
    document.body.style.overflow = "hidden";
  };

  const columns = [
    columnHelper.accessor("images", {
      id: "images",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Name</p>
      ),
      cell: (info) => {
        const name = info?.row?.original?.accountDetails?.name;
        const username = info?.row?.original?.accountDetails?.username;
        const title = name || username || "--";

        return (
          <div className="flex items-center gap-3">
            {info.getValue()?.[0] ? (
              <img
                src={
                  info.getValue()?.[0]?.includes("http")
                    ? info.getValue()?.[0]
                    : process.env.REACT_APP_BASE_URL + info.getValue()?.[0]
                }
                alt="Profile"
                className="!h-8 !w-8 rounded-lg object-cover"
              />
            ) : (
              <Avatar name={title} className="!h-8 !w-8 rounded-lg object-cover" />
            )}
            <p>{title}</p>
          </div>
        );
      },
    }),
    columnHelper.accessor("accountdetail", {
      id: "accountdetail",
      header: () => (
        <p className="text-sm font-bold uppercase text-white pl-5">Phone Number</p>
      ),
      cell: (info) => {
        const title = info?.row?.original?.accountDetails?.phone;

        return <p className="text-sm text-white">{title ? title : "--"}</p>;
      },
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => (
        <p className="text-sm font-bold uppercase text-white pl-4">Description</p>
      ),
      cell: (info) => (
        <div
          title={info.getValue()}
          className="line-clamp-2 w-[200px] items-center overflow-hidden text-ellipsis whitespace-normal"
        >
          {info.getValue() ? info.getValue() : "--"}
          {/* <Progress width="w-[108px]" value={info.getValue()} /> */}
        </div>
      ),
    }),
    columnHelper.accessor("engine_cc", {
      id: "engine_cc",
      header: () => (
        <p className="text-sm font-bold uppercase text-white pl-4">Engine cc</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <p className="text-sm text-white">
            {info.getValue() ? info.getValue() : "--"}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("make", {
      id: "make",
      header: () => (
        <p className="text-sm font-bold uppercase text-white pl-4">Make</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? info.getValue() : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => (
        <p className="text-sm font-bold uppercase text-white pl-4">Created At</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue()
            ? moment(info.getValue()).format("DD/MM/YYYY hh:mm A")
            : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => (
        <p className="text-sm font-bold uppercase text-white pl-4">Other</p>
      ),
      cell: (info) => (
        <div className="flex items-center overflow-hidden">
          <span className="truncate">
            {info.getValue()
              ? moment(info.getValue()).format("DD/MM/YYYY hh:mm A")
              : "--"}
          </span>
        </div>
      ),
    }),
  ]; // eslint-disable-next-line

  const [profileData, setProfileData] = useState([]);
  // Initialize from cache if available
  const [data, setData] = useState(tabCache.exploreGarages.data || []);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const getAllOrganizationEvents = async (isSearching = false) => {
    const isFirstLoad = tabCache.exploreGarages.firstLoad;
    
    // Show loader on first load, search, or pagination change
    if (isFirstLoad || isSearching || isPageChanging || isFilterChanging) {
      setIsLoading(true);
    }
    
    try {
      // const body = {
      //   page: page,
      //   size: pageSize,
      //   search: searchText,
      // };
      const filters = {};

      if (dateRangeFromParams) {
        filters.startDate = dateRangeFromParams.startDate;
        filters.endDate = dateRangeFromParams.endDate;
      }

      const body = {
        pagination: {
          page: page,
          pageSize: pageSize,
          projection: {},
          filters: filters,
          sort: {},
        },
        searchtext: encodeURIComponent(searchText),
        ...(dateRangeFromParams
          ? {
              startDate: dateRangeFromParams.startDate,
              endDate: dateRangeFromParams.endDate,
            }
          : {}),
      };
      const response = await OrganizationEventService.getAllListGarages(body);
      const garagesData = response.data.data;
      
      // Update state and cache
      setData(garagesData);
      tabCache.exploreGarages.data = garagesData;
      setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      
      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.exploreGarages.firstLoad = false;
      }
    } catch (error) {
      Toast("error", error.response.data.message);
      const emptyData = [];
      setData(emptyData);
      tabCache.exploreGarages.data = emptyData;
      if (isFirstLoad) {
        tabCache.exploreGarages.firstLoad = false;
      }
    } finally {
      if (isFirstLoad || isSearching || isPageChanging || isFilterChanging) {
        setIsLoading(false);
      }
      setIsPageChanging(false);
      setIsFilterChanging(false);
    }
  };

  useEffect(() => {
    // Check if searchText changed (this indicates a search operation)
    const isSearchChange = prevSearchTextRef.current !== searchText;
    if (isSearchChange) {
      prevSearchTextRef.current = searchText;
    }
    
    // If we have cached data, show it immediately and fetch in background
    if (tabCache.exploreGarages.data) {
      // Data already shown from initial state, just fetch in background
      // Show loader if this is a search operation
      getAllOrganizationEvents(isSearchChange);
    } else {
      // No cached data, fetch and show loader
      getAllOrganizationEvents(false);
    }
    // eslint-disable-next-line
  }, [page, pageSize, searchText, searchParams, dateRangeFromParams]);

  useEffect(() => {
    setIsFilterChanging(true);
    setPage(1);
  }, [searchParams]);

  const handlePageChange = (newPage) => {
    setIsPageChanging(true);
    setPage(newPage);
  };

  const handleToggleActivation = async () => {
    setConfirmLoader(true);
    try {
      const body = {
        req_id: id,
        status: selectedUser === "approved" ? "rejected" : "approved",
      };
      const response =
        await OrganizationEventService.updateOrganizationEventsDetails(body);
      setPopupOpen(false);
      document.body.style.overflow = "unset";
      getAllOrganizationEvents();
      Toast("success", "Event Updated Successfully!");
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfilePopup(false);
        document.body.style.overflow = "unset";
      }
      if (activateRef.current && !activateRef.current.contains(event.target)) {
        setPopupOpen(false);
        document.body.style.overflow = "unset";
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDelete = async () => {
    setConfirmLoader(true);
    try {
      const body = {
        car_id: id,
      };
      const response = await OrganizationEventService.deleteGarage(body);
      setPopupOpenDelete(false);
      document.body.style.overflow = "unset";
      getAllOrganizationEvents();
      Toast("success", response.data.message);
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  return (
    <div className="mt-0.3 grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        {/* <ComplexTable
          columnsData={columnsDataComplex}
          tableData={tableDataComplex}
        /> */}

        <Card extra={"w-full h-full px-6 pb-6 overflow-x-auto"}>
          {/* <div className="relative flex items-center justify-between pt-4">
            <div className="text-xl font-bold text-white">Users</div>
          </div> */}

          <div className="mt-4 -ml-2 flex justify-start">
            <div className="flex items-center rounded-full bg-navy-900 py-3 text-white xl:w-[180px]">
              <p className="pl-3 pr-2 text-xl">
                <FiSearch className="h-4 w-4 text-white" />
              </p>
              <input
                type="text"
                placeholder="Search..."
                className="block w-full bg-navy-900 text-sm font-medium max-w-[120px] text-white outline-none placeholder:!text-white sm:w-fit"
                onChange={handleSearchInputChange}
                value={searchInputValue}
              />
            </div>
          </div>

          <div
            className={`table-scroll mt-4 -mx-6 relative ${
              isLoading
                ? "xxs:h-[40vh] xs:h-[58vh] sm:h-[65vh] overflow-hidden"
                : "overflow-x-auto"
            }`}
          >
            {isLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            <table
              className={`w-full ${isLoading ? "table-fixed h-full" : ""}`}
              style={
                isLoading
                  ? { tableLayout: "fixed", width: "100%", height: "100%" }
                  : { tableLayout: "auto", minWidth: "100%" }
              }
            >
              <thead className={isLoading ? "hidden" : ""}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map((header) => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          className="cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start first:pl-6 last:pr-6"
                        >
                          <div className="items-center justify-between text-xs text-gray-200">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody className={isLoading ? "h-full" : ""}>
                {isLoading ? (
                  <tr className="md:hidden h-full">
                    <td colSpan={columns.length} className="h-full">
                      <div className="flex h-full items-center justify-center">
                        <img src={Loader} alt="Loading..." className="h-16 w-16" />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => {
                    const rowId = row.original._id;
                    return (
                      <tr
                        key={row.id}
                        className="group relative border-b border-[#fbfbfb1a] last:border-none hover:bg-[#8E8E8E]/5 hover:!border-b-0 transition-colors cursor-pointer"
                      >
                        {row.getVisibleCells().map((cell, cellIndex) => {
                          const isFirst = cellIndex === 0;
                          const isLast = cellIndex === row.getVisibleCells().length - 1;
                          return (
                            <td
                              key={cell.id}
                              className={`relative min-w-[150px] border-white/0 py-3 ${isFirst ? 'pl-6' : 'pl-4'} ${isLast ? 'pr-16 group-hover:pr-16' : 'pr-4'}`}
                            >
                              <div className={`overflow-hidden ${isLast ? 'pr-2' : ''}`}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </div>
                              {isLast && (
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                  <div
                                    className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[rgba(255,37,37,0.1)] text-[rgba(255,44,57,1)] shadow-lg flex-shrink-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenDelete(rowId, row.original.is_active, row.original);
                                    }}
                                  >
                                    {isViewLoading && rowId === id ? (
                                      <img
                                        src={Loader}
                                        alt="Loading..."
                                        className="mx-auto h-4 w-4"
                                      />
                                    ) : (
                                      <MdDeleteForever size={20} />
                                    )}
                                  </div>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="py-4 text-center">
                      <p className="my-3">Data Not Found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {table.getRowModel().rows.length > 0 && !isLoading && (
            <Pagination
              page={page}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          )}

          {popupOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
              <div
                className="w-96 rounded-xl bg-navy-800 p-6 shadow-lg"
                ref={activateRef}
              >
                <h2 className="mb-4 text-xl font-bold text-white">
                  Confirm Action
                </h2>
                <p className="mb-6 text-gray-600">
                  Are you sure you want to{" "}
                  {selectedUser === "approved" ? "Reject" : "Approve"} this
                  event?
                </p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setPopupOpen(false);
                      document.body.style.overflow = "unset";
                    }}
                    className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleToggleActivation();
                    }}
                    className="w-24 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90"
                  >
                    {confirmLoader ? (
                      <img
                        src={Loader}
                        alt="Loading..."
                        className="mx-auto h-6 w-6"
                      />
                    ) : (
                      "Confirm"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* <Modal
            isOpen={popupOpen}
            onClose={() => {
              setPopupOpen(false);
              document.body.style.overflow = "unset";
            }}
            isActive={selectedUser}
            onToggle={handleToggleActivation}
            activateRef={activateRef}
            confirmLoader={confirmLoader}
          /> */}
          {popupOpenDelete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
              <div
                className="w-96 rounded-xl bg-navy-800 p-6 shadow-lg"
                ref={activateRef}
              >
                <h2 className="mb-4 text-xl font-bold text-white">Delete</h2>
                <p className="mb-6 text-gray-600">
                  Are you sure you want to delete this garage?
                </p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setPopupOpenDelete(false);
                      document.body.style.overflow = "unset";
                    }}
                    className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleDelete();
                    }}
                    className="w-24 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90"
                  >
                    {confirmLoader ? (
                      <img
                        src={Loader}
                        alt="Loading..."
                        className="mx-auto h-6 w-6"
                      />
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ListGarages;
